import React, { Fragment, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import BackToTop from "../../components/BackToTop/BackToTop";
import BlogBodyRenderer from "./BlogBodyRenderer";
import blogServices from "../../apiServices/blogServices";
import "./style.css";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatReadTime = (blog) => {
  if (blog.blogReadTime) return `${blog.blogReadTime} min read`;
  const words = (blog.bodyMarkdown || "").trim().split(/\s+/).length;
  return `${Math.max(1, Math.ceil(words / 200))} min read`;
};

const BlogDetailsPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setLoaded(false);
    setNotFound(false);

    const fetchBlog = async () => {
      try {
        let res;
        try {
          res = await blogServices.getBlogBySlug(slug);
        } catch (slugError) {
          // Blogs without a slug are still reachable by numeric id.
          if (!/^\d+$/.test(slug)) throw slugError;
          res = await blogServices.getBlogById(Number(slug));
        }

        if (!isMounted) return;

        if (res?.data?.status) {
          setBlog(res.data.data);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        if (isMounted) setNotFound(true);
      } finally {
        if (isMounted) setLoaded(true);
      }
    };

    fetchBlog();
    window.scrollTo(0, 0);

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (!loaded) {
    return (
      <Fragment>
        <Navbar />
        <div className="tp-blog-details-loading">
          <p>Loading...</p>
        </div>
        <Footer />
      </Fragment>
    );
  }

  if (notFound || !blog) {
    return (
      <Fragment>
        <Navbar />
        <div className="tp-blog-details-loading">
          <h2>Blog not found</h2>
          <Link to="/" className="blog-read-more">
            &#8592; Back to home
          </Link>
        </div>
        <Footer />
      </Fragment>
    );
  }

  const heroImage = (blog.blogImages || []).find((img) => img.isHeroImage) ||
    (blog.blogImages || [])[0];
  const tags = blog.tags
    ? blog.tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];
  const tocSections = Array.isArray(blog.tocSections) ? blog.tocSections : [];
  const faqs = Array.isArray(blog.faqs) ? blog.faqs : [];
  const authors = Array.isArray(blog.authors) ? blog.authors : [];

  return (
    <Fragment>
      <Navbar />

      <section className="tp-blog-details-area section-padding">
        <div className="container">
          <nav className="tp-blog-breadcrumb">
            <Link to="/">Home</Link>
            <span> / </span>
            <Link to="/">Blog</Link>
            <span> / </span>
            <span>{blog.title}</span>
          </nav>

          <div className="row justify-content-center">
            <div className="col-lg-9 col-12">
              <div className="tp-blog-details-hero">
                {heroImage?.image ? (
                  <img
                    src={heroImage.image}
                    alt={heroImage.altText || blog.title}
                  />
                ) : (
                  <div className="blog-img-placeholder" />
                )}
              </div>

              <ul className="tp-blog-details-meta">
                <li>{formatReadTime(blog)}</li>
                {blog.publishedDateTime && (
                  <li>{formatDate(blog.publishedDateTime)}</li>
                )}
                {blog.views != null && <li>{blog.views} views</li>}
              </ul>

              <h1 className="tp-blog-details-title">{blog.title}</h1>

              {blog.subTitle && (
                <p className="tp-blog-details-subtitle">{blog.subTitle}</p>
              )}

              {tags.length > 0 && (
                <div className="tp-blog-details-tags">
                  {tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              )}

              <hr />

              <div className="tp-blog-details-body">
                {tocSections.length > 0 ? (
                  tocSections.map((section, idx) => (
                    <div key={idx} className="tp-blog-details-section">
                      {section.heading && <h2>{section.heading}</h2>}
                      {section.bodyBlocks ? (
                        <BlogBodyRenderer bodyBlocks={section.bodyBlocks} />
                      ) : (
                        <ReactMarkdown>{section.body || ""}</ReactMarkdown>
                      )}
                    </div>
                  ))
                ) : (
                  <ReactMarkdown>{blog.bodyMarkdown || ""}</ReactMarkdown>
                )}
              </div>

              {faqs.length > 0 && (
                <div className="tp-blog-details-faqs">
                  <h3>Frequently Asked Questions</h3>
                  {faqs.map((faq, idx) => (
                    <div key={idx} className="tp-blog-details-faq-item">
                      <h4>{faq.question}</h4>
                      <p>{faq.answer}</p>
                    </div>
                  ))}
                </div>
              )}

              {authors.length > 0 && (
                <div className="tp-blog-details-authors">
                  <span>By </span>
                  {authors.map((author, idx) => (
                    <span key={author.adminAuthorId}>
                      {author.authorName}
                      {idx < authors.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
              )}

              {(blog.ctaHeading || blog.ctaButtonName) && (
                <div className="tp-blog-details-cta">
                  {blog.ctaHeading && <h3>{blog.ctaHeading}</h3>}
                  {blog.ctaSubHeading && <p>{blog.ctaSubHeading}</p>}
                  {blog.ctaUrl ? (
                    <a
                      href={blog.ctaUrl}
                      className="theme-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {blog.ctaButtonName || "Get In Touch"}
                    </a>
                  ) : (
                    <Link to="/" className="theme-btn">
                      {blog.ctaButtonName || "Get In Touch"}
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </Fragment>
  );
};

export default BlogDetailsPage;
